import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';

import firstWord from 'common/util/firstWord';

import {
  USER_ROLE_RENTER,
  USER_ROLE_SUNROOM_CLUB,
} from 'common/userRoles';

import {
  getApplicationFeeUsdString,
  findHouseholdApplication,
} from 'common/householdApplication/applicationHelpers';

import {
  HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
} from 'common/householdConstants';

import {
  SR_API_ACTION_HOUSEHOLD_PAY_APPLICATION,
  SR_API_ACTION_GET_LISTING_BY_ID,
} from 'api/actionTypes';

import {
  SCREEN_ID_APPLICATION_PAID,
} from 'client/constants/screenIds';

import log from 'client/lib/log';
import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedRoles from 'client/helpers/allowedRoles';

import payApplicationAction from 'client/actions/Auth/payApplication';

import SrStripeCardForm from 'client/components/SrStripeCardForm';
import Navbar from 'client/components/Navbar';
import SrNavbarTitle from 'client/components/SrNavbarTitle';

import ListingActions from 'client/actions/Listing';

@withTransitionHook
@allowedRoles({
  roles: [
    USER_ROLE_RENTER,
  ],
  redirectUrl: '/login',
  addRedirectQueryParam: true,
})
@withApiState({
  householdPayApplicationApiState: {
    action: SR_API_ACTION_HOUSEHOLD_PAY_APPLICATION,
  },
  getListingApiState: {
    action: SR_API_ACTION_GET_LISTING_BY_ID,
  },
})
@branch({
  listing: ['listing'],
  currentUser: ['currentUser'],
  householdApplications: ['householdApplications'],
})
@withScrollToTop
@withScreenId(SCREEN_ID_APPLICATION_PAID)
class ApplicationPaymentPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Application Submitted | Sunroom',
  })

  static propTypes = {
    listing: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    householdApplications: PropTypes.array,
    householdPayApplicationApiState: PropTypes.object.isRequired,
    getListingApiState: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
  }

  static fetchData(match, inject) {
    return ListingActions.getById(inject, parseInt(match.params.listingId, 10));
  }

  _getHouseholdApplication = () => {
    const {
      householdApplications,
      match: { params },
    } = this.props;

    return findHouseholdApplication({
      householdApplications: householdApplications,
      listingId: parseInt(params.listingId, 10),
      category: HOUSEHOLD_APPLICATION_CATEGORY_HOUSEHOLD_APPLICATION,
    });
  }

  _onPaymentFormSubmit = (source) => {
    const { dispatch, history, match: { params } } = this.props;
    const householdApplication = this._getHouseholdApplication();

    dispatch(payApplicationAction, {
      source: source,
      applicationId: householdApplication.id,
    }).then(() => {
      history.push(`/application/${params.userId}/${params.listingId}/pay`);
    }).catch((error) => {
      log.error(error);
    });
  }

  render() {
    const {
      listing,
      currentUser,
      householdPayApplicationApiState,
      getListingApiState,
    } = this.props;

    const householdApplication = this._getHouseholdApplication();

    if (getListingApiState.inProgress || !listing) {
      return (
        <div className='loading-gif' />
      );
    }

    if (!householdApplication) {
      return (
        <Redirect to='/tour' />
      );
    }

    const originalApplicationFeeUsd = listing.applicationFee || 0;

    const applicationFeeUsdString = getApplicationFeeUsdString({
      applicationFee: originalApplicationFeeUsd,
      user: currentUser,
    });

    const finalApplicationFeeUsd = currentUser.roles.includes(USER_ROLE_SUNROOM_CLUB)
      ? applicationFeeUsdString
      : originalApplicationFeeUsd;

    return (
      <div className='page-payApplicationFeeForm'>
        <Navbar
          customLogo={(
            <SrNavbarTitle title={`${firstWord(currentUser.name).toLowerCase()}'s application`} />
          )}
        />
        <div className='formProgressive'>
          <div className='contentContainer'>
            {currentUser.roles.includes(USER_ROLE_SUNROOM_CLUB) && (
              <span className='headline'>
                <span>Pay </span>
                <span className='crossedOut color-grey'>{`$${originalApplicationFeeUsd}`}</span>
                <span className='color-green bold'>{` $${finalApplicationFeeUsd} `}</span>
                <br />
                <span>application fee</span>
              </span>
            )}
            {!currentUser.roles.includes(USER_ROLE_SUNROOM_CLUB) && (
              <span className='headline'>{`Pay $${finalApplicationFeeUsd} application fee`}</span>
            )}
            <span className='explainer'>
              The <span className='bold'>landlord requests each applicant over the age of 18</span>
              {
                ' to pay an application fee. ' +
                'Sunroom does not make any money from this. ' +
                'Please use your credit/debit card to securely pay.'
              }
            </span>
            {householdApplication && (
              <div>
                <SrStripeCardForm
                  onSubmit={this._onPaymentFormSubmit}
                  isSubmitting={householdPayApplicationApiState.inProgress}
                  submitErrorMessage={householdPayApplicationApiState.error
                    ? 'Sorry, the card did not work. Please try another card.'
                    : null
                  }
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

ApplicationPaymentPage.displayName = 'ApplicationPaymentPage';

export default ApplicationPaymentPage;
